import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgPen, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import SearchableMultiSelectModal from "../Commaon/SearchableMultiSelectModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (value.$oid) return value.$oid;
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
    if (typeof value.toString === "function") {
      const stringValue = value.toString();
      return stringValue === "[object Object]" ? "" : stringValue;
    }
  }
  return String(value);
};

const emptyForm = {
  groupName: "",
  groupType: "test",
  itemIds: [],
  isActive: true,
};

const GroupingsList = () => {
  const [groups, setGroups] = useState([]);
  const [tests, setTests] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [formModal, setFormModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [errors, setErrors] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/grouping?page=${pageNo}&limit=${limit}&search=${encodeURIComponent(query.trim())}`,
        { method: "GET" }
      );
      setGroups(getRows(response.data));
      setTotalRecords(response.data?.tr || 0);
    } catch (error) {
      setGroups([]);
      setTotalRecords(0);
      showSnack("Error fetching groups", "error");
    } finally {
      setLoading(false);
    }
  }, [pageNo, limit, query]);

  const fetchOptions = useCallback(async () => {
    try {
      const [testsResponse, packagesResponse] = await Promise.all([
        AuthInterceptor("/admin/test?page=1&limit=500", { method: "GET" }),
        AuthInterceptor("/admin/package?page=1&limit=500", { method: "GET" }),
      ]);
      setTests(getRows(testsResponse.data));
      setPackages(getRows(packagesResponse.data));
    } catch (error) {
      setTests([]);
      setPackages([]);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  // Server-side pagination: selection only covers the rows of the current page/search.
  useEffect(() => {
    setSelectedIds([]);
  }, [pageNo, limit, query]);

  const options = useMemo(
    () =>
      form.groupType === "package"
        ? packages.map((pkg) => ({
            value: normalizeId(pkg._id),
            label: pkg.title || pkg.name || normalizeId(pkg._id),
          }))
        : tests.map((test) => ({
        value: normalizeId(test._id),
        label: test.title || test.name || normalizeId(test._id),
          })),
    [form.groupType, tests, packages]
  );

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setErrors({});
  };

  const openAdd = () => {
    resetForm();
    setFormModal(true);
  };

  const openEdit = (group) => {
    setEditId(group._id);
    const groupType = group.groupType || (group.packageIds?.length ? "package" : "test");
    setForm({
      groupName: group.groupName || "",
      groupType,
      itemIds: (group.itemIds || (groupType === "package" ? group.packageIds : group.testIds) || []).map(normalizeId),
      isActive: group.isActive !== false,
    });
    setErrors({});
    setFormModal(true);
  };


  const validateForm = () => {
    const nextErrors = {};
    if (!form.groupName.trim()) nextErrors.groupName = "Group name is required";
    if (!form.itemIds.length) {
      nextErrors.items = `Select at least one ${form.groupType}`;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitForm = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      const response = await AuthInterceptor(
        editId ? `/admin/grouping/${editId}` : "/admin/grouping",
        {
          method: editId ? "PUT" : "POST",
          body: JSON.stringify({
            ...form,
            testIds: form.groupType === "test" ? form.itemIds : [],
            packageIds: form.groupType === "package" ? form.itemIds : [],
          }),
        }
      );
      if ([200, 201].includes(response.data?.s)) {
        showSnack(editId ? "Group updated successfully" : "Group added successfully");
        setFormModal(false);
        resetForm();
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error saving group", "error");
      }
    } catch (error) {
      showSnack("Error saving group", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (group) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/grouping/${group._id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ isActive: !group.isActive }),
      });
      if (response.data?.s === 200) {
        showSnack("Group status updated");
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error updating status", "error");
      }
    } catch (error) {
      showSnack("Error updating status", "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async () => {
    if (!deleteId) return;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/grouping/${deleteId}`, {
        method: "DELETE",
      });
      if (response.data?.s === 200) {
        showSnack("Group deleted successfully");
        fetchGroups();
      } else {
        showSnack(response.data?.m || "Error deleting group", "error");
      }
    } catch (error) {
      showSnack("Error deleting group", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const totalPages = Math.ceil(totalRecords / limit) || 1;

  const visibleIds = groups.map((group) => group._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      showSnack("Select at least one group", "error");
      return;
    }
    if (action === "delete") {
      setBulkDeleteModel(true);
      return;
    }

    const isActive = action === "activate";
    // Skip groups that are already in the target state.
    const ids = selectedIds.filter((id) => groups.find((g) => g._id === id)?.isActive !== isActive);

    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) =>
          AuthInterceptor(`/admin/grouping/${id}/status`, {
            method: "PATCH",
            body: JSON.stringify({ isActive }),
          })
        )
      );
      const failed = results.filter(
        (result) => result.status !== "fulfilled" || result.value?.data?.s !== 200
      ).length;
      if (failed) {
        showSnack(`Updated ${ids.length - failed} of ${ids.length} groups`, "error");
      } else {
        showSnack("Bulk action completed");
      }
      setSelectedIds([]);
      fetchGroups();
    } finally {
      setLoading(false);
    }
  };

  const bulkDeleteGroups = async () => {
    const ids = [...selectedIds];
    try {
      setLoading(true);
      const results = await Promise.allSettled(
        ids.map((id) => AuthInterceptor(`/admin/grouping/${id}`, { method: "DELETE" }))
      );
      const failed = results.filter(
        (result) => result.status !== "fulfilled" || result.value?.data?.s !== 200
      ).length;
      if (failed) {
        showSnack(`Deleted ${ids.length - failed} of ${ids.length} groups`, "error");
      } else {
        showSnack("Groups deleted successfully");
      }
      setSelectedIds([]);
      fetchGroups();
    } finally {
      setLoading(false);
      setBulkDeleteModel(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Grouping"
          searchValue={query}
          searchPlaceholder="Search groups..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        />

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Bulk actions only make sense when there are rows to select */}
          {groups.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
                Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-custom ms-auto" onClick={openAdd}>
            Add Group
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all groups on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Group Name</th>
                <th>Type</th>
                <th>Selected Items</th>
                <th>Items</th>
                <th>Status</th>
                <th style={{ width: "130px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                groups.map((group, index) => (
                  <tr key={group._id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(group._id)}
                        onChange={() => toggleRowSelection(group._id)}
                        aria-label={`Select ${group.groupName || "group"}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td>{group.groupName || "-"}</td>
                    <td>{group.groupType === "package" ? "Package" : "Test"}</td>
                    <td className="text-truncate" title={(group.items || []).map((item) => item.title).join(", ")}>
                      {(group.items || []).map((item) => item.title).join(", ") || "-"}
                    </td>
                    <td>{group.itemCount ?? 0}</td>
                    <td>
                      {group.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleStatus(group)}>
                          Active
                        </span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleStatus(group)}>
                          Inactive
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openEdit(group)}>
                          <CgPen size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteId(group._id)}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {!loading && groups.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    No groups found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={totalRecords > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      <Modal show={formModal} onHide={() => setFormModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editId ? "Edit Group" : "Add Group"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitForm}>
            <div className="mb-3">
              <label className="form-label">Group Name</label>
              <input
                className={`form-control ${errors.groupName ? "is-invalid" : ""}`}
                value={form.groupName}
                onChange={(e) => setForm((prev) => ({ ...prev, groupName: e.target.value }))}
                disabled={loading}
              />
              {errors.groupName && <div className="invalid-feedback">{errors.groupName}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">Select Type</label>
              <select
                className="form-select"
                value={form.groupType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    groupType: e.target.value,
                    itemIds: [],
                  }))
                }
                disabled={loading}
              >
                <option value="test">Test</option>
                <option value="package">Package</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="form-label">{form.groupType === "package" ? "Packages" : "Tests"}</label>
              <SearchableMultiSelectModal
                title={`Select ${form.groupType === "package" ? "Packages" : "Tests"}`}
                options={options}
                selectedValues={form.itemIds}
                onChange={(itemIds) => setForm((prev) => ({ ...prev, itemIds }))}
                buttonLabel={`Select ${form.groupType === "package" ? "packages" : "tests"}`}
                emptyLabel="No items selected"
                disabled={loading}
              />
              {errors.items && <div className="text-danger mt-1">{errors.items}</div>}
            </div>

            <div className="mb-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={form.isActive ? "true" : "false"}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.value === "true" }))}
                disabled={loading}
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {editId ? "Update Group" : "Add Group"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={Boolean(deleteId)}
        count={1}
        singular="group"
        plural="groups"
        loading={loading}
        onConfirm={deleteGroup}
        onCancel={() => setDeleteId(null)}
      />

      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular="group"
        plural="groups"
        loading={loading}
        onConfirm={bulkDeleteGroups}
        onCancel={() => setBulkDeleteModel(false)}
      />
    </>
  );
};

export default GroupingsList;
